import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DivisionMileStone, DivisionMileStones } from "../../api/divisionMileStones/types";
import { createDivisionMileStone, createMileStonesBatch, deleteDivisionMileStone, getdivisionMilestones, updateDivisionMileStone, updateMileStonesBatch } from "../../api/divisionMileStones/divisionMileStonesApi";

/**
 * Custom React Query hook to fetch division milestones for a given division code.
 * 
 * @param divCode - The division code to filter milestones by.
 * @returns Query result containing DivisionMileStones data, loading and error states, etc.
 */
export const useDivisionMileStoneData = (divCode: string) => {
     return useQuery<DivisionMileStones>({
          queryKey: ["divisionMileStones", divCode],
          queryFn: () => getdivisionMilestones({ filter: `divisionCode=${divCode}` }),
          enabled: !!divCode,
          staleTime: 1000 * 60 * 15, // 15 minutes
          gcTime: 1000 * 60 * 15, // 15 minutes
     });
};

export const useDivisionMileStoneDataReleased = (divCode: string) => {
     return useQuery<DivisionMileStones>({
          queryKey: ["divisionMileStonesReleased", divCode],
          queryFn: () => getdivisionMilestones({ filter: `divisionCode=${divCode},mode=released` }),
          enabled: !!divCode,
          staleTime: 1000 * 60 * 15, // 15 minutes
          gcTime: 1000 * 60 * 15, // 15 minutes
     });
};

/**
 * Custom React Query hook to create a new division milestone.
 * 
 * @returns A mutation function that can be used to create a new division milestone.
 */
export const useCreateDivisionMileStone = () => {
     const queryClient = useQueryClient();
     return useMutation<DivisionMileStone, Error, DivisionMileStone>({
          mutationFn: (divisionMileStone) => createDivisionMileStone(divisionMileStone),
          onSuccess: (newDivisionMileStone) => {
               queryClient.setQueryData<DivisionMileStones>(["divisionMileStonesReleased", newDivisionMileStone.divisionCode], (oldData) => {
                    if (!oldData) {
                         return new DivisionMileStones("1", [newDivisionMileStone]);
                    }
                    if (Array.isArray(oldData.data)) {
                         const newData = [...oldData.data, newDivisionMileStone];
                         const existingCount = parseInt(oldData.counts) || 0;
                         const newCount = (existingCount + 1).toString();
                         return new DivisionMileStones(newCount, newData);
                    }
                    return new DivisionMileStones("1", [newDivisionMileStone]);
               });
          },
          onError: (error) => {
               console.error("Error creating division milestone:", error);
          }
     });
};

/**
 * Custom React Query hook to update an existing division milestone.
 * 
 * @returns A mutation function that can be used to update an existing division milestone.
 */
export const useUpdateDivisionMileStone = () => {
     const queryClient = useQueryClient();
     return useMutation<DivisionMileStone, Error, DivisionMileStone>({
          mutationFn: (divisionMileStone) => updateDivisionMileStone(divisionMileStone),
          onSuccess: (newDivisionMileStone) => {
               queryClient.setQueryData<DivisionMileStones>(["divisionMileStonesReleased", newDivisionMileStone.divisionCode], (oldData) => {
                    if (!oldData) {
                         return new DivisionMileStones("1", [newDivisionMileStone]);
                    }
                    if (Array.isArray(oldData.data)) {
                         const newData = oldData.data.map((d) => (d.id === newDivisionMileStone.id ? newDivisionMileStone : d));
                         return new DivisionMileStones(newData.length.toString(), newData);
                    }
                    return new DivisionMileStones("1", [newDivisionMileStone]);
               });
          },
          onError: (error) => {
               console.error("Error updating division milestone:", error);
          },
     });
};

/**
 * Custom React Query hook to update multiple division milestones in batch.
 * 
 * @returns A mutation function that can be used to update multiple division milestones.
 */
export const useUpdateMileStonesBatch = () => {
     const queryClient = useQueryClient();
     return useMutation<DivisionMileStone[], Error, { milestones: DivisionMileStone[], mode: 'draft' | 'released', workFlowId: string }>({
          mutationFn: ({ milestones, mode, workFlowId }) => updateMileStonesBatch(milestones, mode, workFlowId),
          onSuccess: (updatedDivisionMileStones, variables) => {
               const divisionCodes = [...new Set(variables.milestones.map(m => m.divisionCode))];
               divisionCodes.forEach(divisionCode => {
                    if (variables.mode === 'released') {
                         queryClient.setQueryData<DivisionMileStones>(["divisionMileStonesReleased", divisionCode], (oldData) => {
                              if (!oldData) {
                                   return new DivisionMileStones(updatedDivisionMileStones.length.toString(), updatedDivisionMileStones);
                              }
                              if (Array.isArray(oldData.data)) {
                                   const newData = oldData.data.map((d) => {
                                        const updated = updatedDivisionMileStones.find(u => u.id === d.id);
                                        return updated || d;
                                   });
                                   return new DivisionMileStones(newData.length.toString(), newData);
                              }
                              return new DivisionMileStones(updatedDivisionMileStones.length.toString(), updatedDivisionMileStones);
                         });
                    }
                    queryClient.setQueryData<DivisionMileStones>(["divisionMileStones", divisionCode], (oldData) => {
                         if (!oldData) {
                              return new DivisionMileStones(updatedDivisionMileStones.length.toString(), updatedDivisionMileStones);
                         }
                         if (Array.isArray(oldData.data)) {
                              const newData = oldData.data.map((d) => {
                                   const updated = updatedDivisionMileStones.find(u => u.id === d.id);
                                   return updated || d;
                              });
                              return new DivisionMileStones(newData.length.toString(), newData);
                         }
                         return new DivisionMileStones(updatedDivisionMileStones.length.toString(), updatedDivisionMileStones);
                    });
               });
          },
          onError: (error, variables) => {
               console.error("Error updating division milestones in batch:", error);
               if (variables && variables.milestones) {
                    const divisionCodes = [...new Set(variables.milestones.map(m => m.divisionCode))];
                    divisionCodes.forEach(divisionCode => {
                         if (variables.mode === 'released') {
                              queryClient.invalidateQueries({ queryKey: ["divisionMileStonesReleased", divisionCode] });
                         }
                         queryClient.invalidateQueries({ queryKey: ["divisionMileStones", divisionCode] });
                    });
               }
          }
     });
};

/**
 * Custom React Query hook to create multiple division milestones in batch.
 * 
 * @returns A mutation function that can be used to create multiple division milestones.
 */
export const useCreateMileStonesBatch = () => {
     const queryClient = useQueryClient();
     return useMutation<DivisionMileStone[], Error, { milestones: DivisionMileStone[], mode: 'draft' | 'released', workFlowId: string }>({
          mutationFn: ({ milestones, mode, workFlowId }) => createMileStonesBatch(milestones, mode, workFlowId),
          onSuccess: (newDivisionMileStones, variables) => {
               const divisionCodes = [...new Set(variables.milestones.map(m => m.divisionCode))];
               divisionCodes.forEach(divisionCode => {
                    if (variables.mode === 'released') {
                         queryClient.setQueryData<DivisionMileStones>(["divisionMileStonesReleased", divisionCode], (oldData) => {
                              if (!oldData) {
                                   return new DivisionMileStones(newDivisionMileStones.length.toString(), newDivisionMileStones);
                              }
                              if (Array.isArray(oldData.data)) {
                                   const newData = [...oldData.data, ...newDivisionMileStones];
                                   return new DivisionMileStones(newData.length.toString(), newData);
                              }
                              return new DivisionMileStones(newDivisionMileStones.length.toString(), newDivisionMileStones);
                         });
                    }
               });
               divisionCodes.forEach(divisionCode => {
                    queryClient.setQueryData<DivisionMileStones>(["divisionMileStones", divisionCode], (oldData) => {
                         if (!oldData) {
                              return new DivisionMileStones(newDivisionMileStones.length.toString(), newDivisionMileStones);
                         }
                         if (Array.isArray(oldData.data)) {
                              const newData = [...oldData.data, ...newDivisionMileStones];
                              return new DivisionMileStones(newData.length.toString(), newData);
                         }
                         return new DivisionMileStones(newDivisionMileStones.length.toString(), newDivisionMileStones);
                    });
               });
          },
          onError: (error, variables) => {
               console.error("Error creating division milestones in batch:", error);
               if (variables && Array.isArray(variables.milestones)) {
                    const divisionCodes = [...new Set(variables.milestones.map(m => m.divisionCode))];
                    divisionCodes.forEach(divisionCode => {
                         if (variables.mode === 'released') {
                              queryClient.invalidateQueries({ queryKey: ["divisionMileStonesReleased", divisionCode] });
                         }
                         queryClient.invalidateQueries({ queryKey: ["divisionMileStones", divisionCode] });
                    });
               }
          }
     });
};

export const useDeleteDivisionMileStone = () => {
     const queryClient = useQueryClient();
     return useMutation<void, Error, { id: string, divisionCode: string }>({
          mutationFn: ({ id, divisionCode }) => deleteDivisionMileStone(id, divisionCode),
          onSuccess: (_data, variables) => {
               if (variables && variables.divisionCode && variables.id) {
                    queryClient.setQueryData<DivisionMileStones>(["divisionMileStonesReleased", variables.divisionCode], (oldData) => {
                         if (!oldData || !Array.isArray(oldData.data)) {
                              return oldData;
                         }
                         const newData = oldData.data.filter(milestone => milestone.id !== variables.id);
                         return new DivisionMileStones(newData.length.toString(), newData);
                    });
                    queryClient.setQueryData<DivisionMileStones>(["divisionMileStones", variables.divisionCode], (oldData) => {
                         if (!oldData || !Array.isArray(oldData.data)) {
                              return oldData;
                         }
                         const newData = oldData.data.filter(milestone => milestone.id !== variables.id);
                         return new DivisionMileStones(newData.length.toString(), newData);
                    });
               }
          },
          onError: (error, variables) => {
               console.error('Error deleting division milestone:', error);
               if (variables && variables.divisionCode) {
                    queryClient.invalidateQueries({ queryKey: ["divisionMileStonesReleased", variables.divisionCode] });
                    queryClient.invalidateQueries({ queryKey: ["divisionMileStones", variables.divisionCode] });
               }
          },
     });
};